import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { CashRegister, Eye, X as XIcon, Pencil, Plus, Minus, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useDataCache } from '@/contexts/DataCache';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CollectShipments = () => {
  const { deliverers, sellers, refreshDeliverers } = useDataCache();
  const [selectedDeliverer, setSelectedDeliverer] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [paymentMethod, setPaymentMethod] = useState('efectivo');
  const [paymentSplit, setPaymentSplit] = useState({ efectivo: 0, tarjeta: 0, transferencia: 0 });
  const [loading, setLoading] = useState(false);
  const [detailDialog, setDetailDialog] = useState({ open: false, shipment: null });
  const [editDialog, setEditDialog] = useState({ open: false, shipment: null, items: [], shipping_cost: 0, search: '', saving: false });
  const { products } = useDataCache();

  useEffect(() => {
    if (selectedDeliverer) {
      fetchShipments(selectedDeliverer.id);
    } else {
      setShipments([]);
      setSelectedIds(new Set());
    }
  }, [selectedDeliverer]);

  const fetchShipments = async (delivererId) => {
    try {
      const { data } = await axios.get(`${API}/shipments/by-deliverer/${delivererId}?unpaid_only=true`, { withCredentials: true });
      setShipments(data);
      setSelectedIds(new Set());
    } catch (error) {
      toast.error('Error al cargar envíos');
    }
  };

  const calcDebt = (sh) => {
    let d = sh.items_total || 0;
    if (!sh.deliverer_keeps_shipping) d += sh.shipping_cost || 0;
    return d;
  };

  const sellerName = (id) => sellers.find(s => s.id === id)?.name || 'N/A';

  const openEditItems = (sh) => {
    setEditDialog({
      open: true,
      shipment: sh,
      items: (sh.items || []).map(it => ({ ...it })),
      shipping_cost: sh.shipping_cost || 0,
      search: '',
      saving: false
    });
  };

  const editTotal = () => editDialog.items.reduce(
    (acc, it) => acc + (parseFloat(it.unit_price) || 0) * (parseInt(it.quantity) || 0),
    0
  );

  const hiddenParentIds = useMemo(() => {
    const parentIds = new Set((products || []).map(p => p.parent_product_id).filter(Boolean));
    return new Set(
      (products || [])
        .filter(p => (p.has_variants || parentIds.has(p.id)) && p.visible_in_pos === false)
        .map(p => p.id)
    );
  }, [products]);

  const editFilteredProducts = () => {
    const q = (editDialog.search || '').trim().toLowerCase();
    if (!q) return [];
    const tokens = q.split(/\s+/).filter(Boolean);
    return (products || [])
      .filter(p =>
        !p.has_variants &&
        p.visible_in_pos !== false &&
        !(p.parent_product_id && hiddenParentIds.has(p.parent_product_id))
      )
      .filter(p => {
        const text = `${p.name || ''} ${p.variant_name || ''} ${p.sku || ''}`.toLowerCase();
        return tokens.every(t => text.includes(t));
      })
      .slice(0, 10);
  };

  const addProductToEdit = (p) => {
    const existing = editDialog.items.findIndex(it => it.product_id === p.id);
    if (existing >= 0) {
      const items = [...editDialog.items];
      items[existing] = { ...items[existing], quantity: (items[existing].quantity || 0) + 1 };
      setEditDialog({ ...editDialog, items, search: '' });
    } else {
      const nm = p.variant_name ? `${p.name} (${p.variant_name})` : p.name;
      setEditDialog({
        ...editDialog,
        items: [...editDialog.items, {
          product_id: p.id,
          product_name: nm,
          quantity: 1,
          price_type: 'precio_1',
          unit_price: parseFloat(p.precio_1 || 0),
          commission: parseFloat(p.default_commission || 0)
        }],
        search: ''
      });
    }
  };

  const updateEditItem = (idx, patch) => {
    const items = [...editDialog.items];
    items[idx] = { ...items[idx], ...patch };
    setEditDialog({ ...editDialog, items });
  };

  const removeEditItem = (idx) => {
    setEditDialog({ ...editDialog, items: editDialog.items.filter((_, i) => i !== idx) });
  };

  const saveEditItems = async () => {
    if (editDialog.items.length === 0) {
      toast.error('Debe haber al menos un producto');
      return;
    }
    setEditDialog(d => ({ ...d, saving: true }));
    try {
      const { data } = await axios.put(`${API}/shipments/${editDialog.shipment.id}/items`, {
        items: editDialog.items.map(it => ({
          product_id: it.product_id,
          product_name: it.product_name,
          quantity: parseInt(it.quantity) || 0,
          price_type: it.price_type || 'precio_1',
          unit_price: parseFloat(it.unit_price) || 0,
          commission: parseFloat(it.commission) || 0
        })),
        shipping_cost: parseFloat(editDialog.shipping_cost) || 0
      }, { withCredentials: true });
      if (data?.status === 'pending') {
        toast.success('✅ Solicitud enviada al admin por Telegram');
      } else {
        toast.success('Envío actualizado');
      }
      setEditDialog({ open: false, shipment: null, items: [], shipping_cost: 0, search: '', saving: false });
      if (selectedDeliverer) fetchShipments(selectedDeliverer.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar cambios');
      setEditDialog(d => ({ ...d, saving: false }));
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const selectAll = () => {
    if (selectedIds.size === shipments.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(shipments.map(s => s.id)));
    }
  };

  const totalToCollect = shipments
    .filter(s => selectedIds.has(s.id))
    .reduce((sum, s) => sum + calcDebt(s), 0);

  const cancelShipment = async (id) => {
    if (!window.confirm('¿Cancelar este envío? Se eliminará la deuda del entregador y se notificará por Telegram (si está vinculado).')) return;
    try {
      await axios.delete(`${API}/shipments/${id}`, { withCredentials: true });
      toast.success('Envío cancelado');
      // refrescar
      await refreshDeliverers();
      if (selectedDeliverer) await fetchShipments(selectedDeliverer.id);
      // limpiar de la selección si estaba
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar envío');
    }
  };

  const handlePay = async () => {    if (selectedIds.size === 0) {
      toast.error('Selecciona al menos un envío');
      return;
    }
    if (paymentMethod === 'mixto') {
      const split = paymentSplit.efectivo + paymentSplit.tarjeta + paymentSplit.transferencia;
      if (split + 0.01 < totalToCollect) {
        toast.error(`Falta dinero por cobrar. Total: $${totalToCollect.toFixed(2)}, recibido: $${split.toFixed(2)}`);
        return;
      }
      const cambio = split - totalToCollect;
      if (cambio > 0.01) {
        const ok = window.confirm(`Cambio a entregar (en efectivo): $${cambio.toFixed(2)}\n\n¿Confirmar cobro?`);
        if (!ok) { return; }
      }
    }
    setLoading(true);
    try {
      const payload = {
        deliverer_id: selectedDeliverer.id,
        shipment_ids: Array.from(selectedIds),
        payment_method: paymentMethod,
        payment_split: paymentMethod === 'mixto' ? paymentSplit : null,
        total: totalToCollect
      };
      await axios.post(`${API}/shipments/pay`, payload, { withCredentials: true });
      toast.success(`Cobrados ${selectedIds.size} envíos por $${totalToCollect.toFixed(2)}`);
      setSelectedIds(new Set());
      setPaymentMethod('efectivo');
      setPaymentSplit({ efectivo: 0, tarjeta: 0, transferencia: 0 });
      // refrescar todo
      await refreshDeliverers();
      const updated = deliverers.find(d => d.id === selectedDeliverer.id);
      if (updated) setSelectedDeliverer({ ...selectedDeliverer, balance_owed: (updated.balance_owed || 0) - totalToCollect });
      await fetchShipments(selectedDeliverer.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cobrar envíos');
    } finally {
      setLoading(false);
    }
  };

  // Resumen del entregador seleccionado
  const summary = selectedDeliverer ? {
    total_envios: shipments.length,
    total_productos: shipments.reduce((s, sh) => s + (sh.items_total || 0), 0),
    total_envio: shipments.filter(sh => !sh.deliverer_keeps_shipping).reduce((s, sh) => s + (sh.shipping_cost || 0), 0),
    deuda_total: shipments.reduce((s, sh) => s + calcDebt(sh), 0)
  } : null;

  return (
    <MainLayout>
      <div className="p-8">
        <div className="mb-6">
          <h1 className="text-4xl font-bold tracking-tight">Cobrar Envíos</h1>
          <p className="text-muted-foreground mt-1">Cobra a los entregadores por los envíos asignados</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left: deliverers list */}
          <div className="lg:col-span-4 space-y-2">
            <h2 className="font-semibold mb-2">Entregadores con deuda</h2>
            {deliverers.filter(d => (d.balance_owed || 0) > 0).map((d) => (
              <button
                key={d.id}
                onClick={() => setSelectedDeliverer(d)}
                data-testid={`deliverer-debt-${d.id}`}
                className={`w-full text-left p-4 border rounded-sm btn-transition ${
                  selectedDeliverer?.id === d.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-slate-50'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold">{d.name}</p>
                    <p className="text-xs text-muted-foreground">{d.phone || 'Sin teléfono'}</p>
                  </div>
                  <span className="text-lg font-bold text-amber-600">${(d.balance_owed || 0).toFixed(2)}</span>
                </div>
              </button>
            ))}
            {deliverers.filter(d => (d.balance_owed || 0) > 0).length === 0 && (
              <p className="text-sm text-muted-foreground p-4 border rounded-sm">
                Ningún entregador tiene deuda pendiente.
              </p>
            )}
          </div>

          {/* Right: shipments table */}
          <div className="lg:col-span-8 space-y-4">
            {!selectedDeliverer ? (
              <div className="p-12 text-center text-muted-foreground border-2 border-dashed rounded-sm">
                Selecciona un entregador para ver sus envíos pendientes.
              </div>
            ) : (
              <>
                {/* Summary */}
                <div className="bg-white border border-border rounded-sm p-4">
                  <h3 className="font-semibold mb-2">{selectedDeliverer.name}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Envíos</p>
                      <p className="text-lg font-bold">{summary.total_envios}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Productos</p>
                      <p className="text-lg font-bold">${summary.total_productos.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Costo envíos</p>
                      <p className="text-lg font-bold">${summary.total_envio.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Deuda total</p>
                      <p className="text-lg font-bold text-amber-600">${summary.deuda_total.toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                {/* Shipments table */}
                <div className="bg-white border border-border rounded-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="collect-shipments-table">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th className="px-3 py-2 text-left">
                            <Checkbox
                              checked={selectedIds.size > 0 && selectedIds.size === shipments.length}
                              onCheckedChange={selectAll}
                              data-testid="select-all-shipments"
                            />
                          </th>
                          <th className="px-3 py-2 text-left">Folio</th>
                          <th className="px-3 py-2 text-left">Cliente</th>
                          <th className="px-3 py-2 text-left">Vendedor</th>
                          <th className="px-3 py-2 text-right">Productos</th>
                          <th className="px-3 py-2 text-right">Envío</th>
                          <th className="px-3 py-2 text-right">Deuda</th>
                          <th className="px-3 py-2"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {shipments.map((sh) => (
                          <tr
                            key={sh.id}
                            className={`hover:bg-slate-50 cursor-pointer ${selectedIds.has(sh.id) ? 'bg-primary/5' : ''}`}
                            onDoubleClick={() => setDetailDialog({ open: true, shipment: sh })}
                          >
                            <td className="px-3 py-2">
                              <Checkbox
                                checked={selectedIds.has(sh.id)}
                                onCheckedChange={() => toggleSelect(sh.id)}
                                data-testid={`select-shipment-${sh.id}`}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                            <td className="px-3 py-2 font-mono">{sh.folio}</td>
                            <td className="px-3 py-2">
                              <div>{sh.client_name}</div>
                              <div className="text-xs text-muted-foreground">{sh.client_phone}</div>
                            </td>
                            <td className="px-3 py-2">{sellerName(sh.seller_id)}</td>
                            <td className="px-3 py-2 text-right">${(sh.items_total || 0).toFixed(2)}</td>
                            <td className="px-3 py-2 text-right">
                              ${(sh.shipping_cost || 0).toFixed(2)}
                              {sh.deliverer_keeps_shipping && (
                                <div className="text-[10px] text-emerald-600">se lo queda</div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right font-semibold">${calcDebt(sh).toFixed(2)}</td>
                            <td className="px-3 py-2">
                              <div className="flex gap-1 justify-end">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => { e.stopPropagation(); setDetailDialog({ open: true, shipment: sh }); }}
                                  data-testid={`view-shipment-collect-${sh.id}`}
                                  title="Ver detalle"
                                >
                                  <Eye size={16} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => { e.stopPropagation(); openEditItems(sh); }}
                                  data-testid={`edit-shipment-items-${sh.id}`}
                                  title="Modificar artículos"
                                  className="text-amber-600 hover:bg-amber-50"
                                >
                                  <Pencil size={16} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={(e) => { e.stopPropagation(); cancelShipment(sh.id); }}
                                  data-testid={`cancel-shipment-collect-${sh.id}`}
                                  title="Cancelar envío"
                                >
                                  <XIcon size={16} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {shipments.length === 0 && (
                          <tr>
                            <td colSpan="8" className="px-3 py-8 text-center text-muted-foreground">
                              Este entregador no tiene envíos pendientes de cobro.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-muted-foreground p-2 border-t">Doble clic en un envío para ver el detalle.</p>
                </div>

                {/* Payment box */}
                {selectedIds.size > 0 && (
                  <div className="bg-white border border-primary rounded-sm p-4 space-y-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-muted-foreground">{selectedIds.size} envíos seleccionados</p>
                        <p className="text-3xl font-bold text-primary">${totalToCollect.toFixed(2)}</p>
                      </div>
                      <CashRegister size={48} className="text-primary/30" />
                    </div>

                    <div>
                      <Label>Método de Pago</Label>
                      <Select value={paymentMethod} onValueChange={(v) => {
                        setPaymentMethod(v);
                        if (v === 'mixto') setPaymentSplit({ efectivo: totalToCollect, tarjeta: 0, transferencia: 0 });
                      }}>
                        <SelectTrigger data-testid="collect-payment-method">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="efectivo">Efectivo</SelectItem>
                          <SelectItem value="tarjeta">Tarjeta</SelectItem>
                          <SelectItem value="transferencia">Transferencia</SelectItem>
                          <SelectItem value="mixto">Mixto</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {paymentMethod === 'mixto' && (
                      <div className="space-y-2 border rounded p-3">
                        <p className="text-sm font-medium">Dividir Pago</p>
                        <div>
                          <Label>Efectivo</Label>
                          <Input
                            type="number" step="0.01"
                            value={paymentSplit.efectivo}
                            onChange={(e) => setPaymentSplit({ ...paymentSplit, efectivo: parseFloat(e.target.value) || 0 })}
                          />
                        </div>
                        <div>
                          <Label>Tarjeta</Label>
                          <Input
                            type="number" step="0.01"
                            value={paymentSplit.tarjeta}
                            onChange={(e) => setPaymentSplit({ ...paymentSplit, tarjeta: parseFloat(e.target.value) || 0 })}
                          />
                        </div>
                        <div>
                          <Label>Transferencia</Label>
                          <Input
                            type="number" step="0.01"
                            value={paymentSplit.transferencia}
                            onChange={(e) => setPaymentSplit({ ...paymentSplit, transferencia: parseFloat(e.target.value) || 0 })}
                          />
                        </div>
                        <div className="flex justify-between text-sm pt-2 border-t">
                          <span>Total split:</span>
                          <span className="font-semibold">${(paymentSplit.efectivo + paymentSplit.tarjeta + paymentSplit.transferencia).toFixed(2)}</span>
                        </div>
                        {(() => {
                          const pagado = paymentSplit.efectivo + paymentSplit.tarjeta + paymentSplit.transferencia;
                          const diff = pagado - totalToCollect;
                          if (diff > 0.01) {
                            return (
                              <div className="flex justify-between text-sm mt-1 text-emerald-700 font-bold bg-emerald-50 px-2 py-1 rounded">
                                <span>💵 Cambio (en efectivo):</span>
                                <span>${diff.toFixed(2)}</span>
                              </div>
                            );
                          }
                          if (diff < -0.01) {
                            return (
                              <div className="flex justify-between text-sm mt-1 text-rose-700 font-bold bg-rose-50 px-2 py-1 rounded">
                                <span>⚠️ Falta:</span>
                                <span>${Math.abs(diff).toFixed(2)}</span>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    )}

                    <Button
                      onClick={handlePay}
                      disabled={loading}
                      className="w-full"
                      size="lg"
                      data-testid="confirm-pay-shipments-button"
                    >
                      {loading ? 'Procesando...' : `Cobrar $${totalToCollect.toFixed(2)}`}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Detail Dialog */}
      <Dialog open={detailDialog.open} onOpenChange={(o) => !o && setDetailDialog({ open: false, shipment: null })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalle Envío {detailDialog.shipment?.folio}</DialogTitle>
          </DialogHeader>
          {detailDialog.shipment && (
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">Cliente</p>
                  <p className="font-medium">{detailDialog.shipment.client_name}</p>
                  <p className="text-xs">{detailDialog.shipment.client_phone}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Vendedor</p>
                  <p>{sellerName(detailDialog.shipment.seller_id)}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground">Dirección</p>
                  <p>{detailDialog.shipment.shipping_address}</p>
                </div>
                {detailDialog.shipment.notes && (
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground">Notas</p>
                    <p>{detailDialog.shipment.notes}</p>
                  </div>
                )}
              </div>
              <table className="w-full border text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-2 py-1 text-left">Producto</th>
                    <th className="px-2 py-1 text-right">Cant.</th>
                    <th className="px-2 py-1 text-right">P. Unit.</th>
                    <th className="px-2 py-1 text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {detailDialog.shipment.items?.map((it, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-2 py-1">{it.product_name}</td>
                      <td className="px-2 py-1 text-right">{it.quantity}</td>
                      <td className="px-2 py-1 text-right">${it.unit_price.toFixed(2)}</td>
                      <td className="px-2 py-1 text-right">${(it.unit_price * it.quantity).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="bg-slate-50 p-3 rounded space-y-1">
                <div className="flex justify-between"><span>Productos:</span><span>${(detailDialog.shipment.items_total || 0).toFixed(2)}</span></div>
                <div className="flex justify-between"><span>Costo envío:</span><span>${(detailDialog.shipment.shipping_cost || 0).toFixed(2)}</span></div>
                <div className="flex justify-between font-bold border-t pt-1"><span>Deuda al entregador:</span><span>${calcDebt(detailDialog.shipment).toFixed(2)}</span></div>
                {detailDialog.shipment.deliverer_keeps_shipping && (
                  <p className="text-xs text-emerald-600">El entregador se queda con el costo del envío.</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit shipment items dialog */}
      <Dialog open={editDialog.open} onOpenChange={(o) => !o && setEditDialog({ open: false, shipment: null, items: [], shipping_cost: 0, search: '', saving: false })}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-items-dialog">
          <DialogHeader>
            <DialogTitle>Modificar artículos del envío {editDialog.shipment?.folio}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
              Aquí puedes agregar, quitar o cambiar cantidades. El stock se ajusta automáticamente. Solo aplica a envíos NO cobrados.
            </div>

            {/* Buscar y agregar producto */}
            <div className="border border-border rounded p-2">
              <Label className="text-xs">Buscar producto para agregar</Label>
              <Input
                value={editDialog.search}
                onChange={(e) => setEditDialog({ ...editDialog, search: e.target.value })}
                placeholder="Nombre, SKU o variante…"
                data-testid="edit-search"
              />
              {editFilteredProducts().length > 0 && (
                <div className="mt-2 max-h-40 overflow-y-auto border rounded">
                  {editFilteredProducts().map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => addProductToEdit(p)}
                      className="w-full text-left px-2 py-1.5 hover:bg-violet-50 border-b last:border-0 flex items-center justify-between text-sm"
                      data-testid={`add-edit-prod-${p.id}`}
                    >
                      <div>
                        <div className="font-medium">{p.variant_name ? `${p.name} – ${p.variant_name}` : p.name}</div>
                        <div className="text-xs text-muted-foreground">{p.sku} · stock: {p.stock || 0} · ${parseFloat(p.precio_1 || 0).toFixed(2)}</div>
                      </div>
                      <Plus size={14} className="text-violet-600" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Items actuales */}
            <div className="border rounded">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Producto</th>
                    <th className="px-2 py-1.5 text-center">Cant</th>
                    <th className="px-2 py-1.5 text-right">P. Unit</th>
                    <th className="px-2 py-1.5 text-right">Subtotal</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {editDialog.items.length === 0 && (
                    <tr><td colSpan={5} className="px-2 py-3 text-center text-muted-foreground">Sin productos</td></tr>
                  )}
                  {editDialog.items.map((it, idx) => (
                    <tr key={idx} className="border-b" data-testid={`edit-item-row-${idx}`}>
                      <td className="px-2 py-1.5">{it.product_name || '(sin nombre)'}</td>
                      <td className="px-2 py-1.5 text-center">
                        <div className="inline-flex items-center gap-1">
                          <Button size="sm" variant="ghost" onClick={() => updateEditItem(idx, { quantity: Math.max(1, (it.quantity || 1) - 1) })}><Minus size={12} /></Button>
                          <Input
                            type="number"
                            min="1"
                            step="1"
                            value={it.quantity}
                            onChange={(e) => {
                              const v = e.target.value.replace(/[^\d]/g, '');
                              updateEditItem(idx, { quantity: v === '' ? '' : parseInt(v, 10) });
                            }}
                            onKeyDown={(e) => {
                              if (e.key === '.' || e.key === ',' || e.key === '-' || e.key === 'e') e.preventDefault();
                            }}
                            className="w-16 text-center"
                            data-testid={`edit-item-qty-${idx}`}
                          />
                          <Button size="sm" variant="ghost" onClick={() => updateEditItem(idx, { quantity: (parseInt(it.quantity) || 0) + 1 })}><Plus size={12} /></Button>
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <Input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateEditItem(idx, { unit_price: e.target.value })} className="w-24 ml-auto text-right" />
                      </td>
                      <td className="px-2 py-1.5 text-right font-semibold">
                        ${((parseFloat(it.unit_price) || 0) * (parseInt(it.quantity) || 0)).toFixed(2)}
                      </td>
                      <td className="px-2 py-1.5">
                        <Button size="sm" variant="ghost" onClick={() => removeEditItem(idx)} className="text-red-600 hover:bg-red-50">
                          <Trash size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Costo de envío</Label>
                <Input type="number" step="0.01" value={editDialog.shipping_cost} onChange={(e) => setEditDialog({ ...editDialog, shipping_cost: e.target.value })} data-testid="edit-shipping-cost" />
              </div>
              <div className="text-right self-end">
                <p className="text-sm text-muted-foreground">Total productos</p>
                <p className="text-2xl font-bold text-primary">${editTotal().toFixed(2)}</p>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setEditDialog({ open: false, shipment: null, items: [], shipping_cost: 0, search: '', saving: false })}>Cancelar</Button>
              <Button onClick={saveEditItems} disabled={editDialog.saving} data-testid="save-edit-items">
                {editDialog.saving ? 'Guardando…' : 'Guardar cambios'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};
